<section class="student-import">
  <div class="import-heading">
    <div>
      <h4>Bulk Student Import</h4>
      <small>Upload an Excel workbook to enroll students in bulk</small>
    </div>

    <button
      type="button"
      class="secondary-button"
      [disabled]="isDownloadingTemplate()"
      (click)="downloadTemplate()"
    >
      @if (isDownloadingTemplate()) {
        <app-loading-indicator />
      } @else {
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10 2.5V12.5M10 12.5L6.25 8.75M10 12.5L13.75 8.75"
            stroke="white"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M3.75 14.375V15.625C3.75 16.1428 3.957 16.6395 4.325 17.0057C4.6931 17.3719 5.1924 17.5781 5.7129 17.5781H14.2871C14.8076 17.5781 15.3069 17.3719 15.675 17.0057C16.043 16.6395 16.25 16.1428 16.25 15.625V14.375"
            stroke="white"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <p>Download template</p>
      }
    </button>
  </div>

  @if (!preview() && !isConfirmed()) {
    <form class="import-card" #importForm="ngForm" (ngSubmit)="uploadFile(importForm)">
      <div class="import-filters">
        <div>
          <label for="academicYearId">Academic Year <span class="required-check">*</span></label>
          <select
            required
            name="academicYearId"
            id="academicYearId"
            [ngModel]="academicYearId"
            (ngModelChange)="onAcademicYearChange($event)"
          >
            <option value="" hidden disabled>Select an academic year</option>

            @for (year of academicYears()?.payload; track year.id) {
              <option [value]="year.id">{{ year.name }}</option>
            }
          </select>
        </div>

      </div>

      <div class="file-drop" [ngClass]="{ 'has-file': selectedFile() }">
        <input
          #fileInput
          type="file"
          id="file"
          name="file"
          accept=".xlsx,.xlsm"
          (change)="onFileSelected($event)"
        />

        <label for="file">
          @if (selectedFile()) {
            <p class="file-name">{{ selectedFile()?.name }}</p>
            <small>{{ fileSizeLabel(selectedFile()!.size) }} - ready to validate</small>
          } @else {
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 16V4M12 4L8 8M12 4L16 8"
                stroke="#5C0B13"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M4 15V18C4 19.1046 4.89543 20 6 20H18C19.1046 20 20 19.1046 20 18V15"
                stroke="#5C0B13"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <p>Click to select an Excel file</p>
            <small>Only .xlsx or .xlsm files up to 5 MB, max 1000 rows</small>
          }
        </label>
      </div>

      <div class="import-actions">
        @if (selectedFile()) {
          <button type="button" class="clear-button" (click)="clearUpload(); fileInput.value = ''">
            Clear file
          </button>
        }

        <button
          type="submit"
          class="button"
          [disabled]="importForm.invalid || !selectedFile() || isUploading()"
        >
          @if (isUploading()) {
            <app-loading-indicator />
          } @else {
            Validate file
          }
        </button>
      </div>
    </form>
  }

  @if (preview() && !isConfirmed()) {
    <section class="summary-cards">
      <div class="summary-card total">
        <h4>{{ preview()?.payload?.summary?.total ?? 0 }}</h4>
        <p>Total rows</p>
      </div>
      <div class="summary-card valid">
        <h4>{{ preview()?.payload?.summary?.valid ?? 0 }}</h4>
        <p>Ready to import</p>
      </div>
      <div class="summary-card duplicate">
        <h4>{{ preview()?.payload?.summary?.duplicate ?? 0 }}</h4>
        <p>Duplicates</p>
      </div>
      <div class="summary-card invalid">
        <h4>{{ preview()?.payload?.summary?.invalid ?? 0 }}</h4>
        <p>Invalid</p>
      </div>
    </section>

    <section class="preview-meta">
      <p>
        <span>Academic year:</span> {{ preview()?.payload?.academic_year?.name }}
        @if (preview()?.payload?.term) {
          <span class="divider"></span>
          <span>Term:</span> {{ preview()?.payload?.term?.name }}
        }
        <span class="divider"></span>
        <span>File:</span> {{ preview()?.payload?.batch?.original_filename || 'Uploaded file' }}
      </p>
    </section>

    <section class="table-content">
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ROW</th>
              <th>ADMISSION NO.</th>
              <th>STUDENT NAME</th>
              <th>LEVEL / CLASS</th>
              <th>STATUS</th>
              <th>DETAILS</th>
            </tr>
          </thead>

          <tbody>
            @if (!preview()?.payload?.rows?.length) {
              <tr>
                <td colspan="6" class="empty">
                  <app-not-found-error>NO ROWS FOUND</app-not-found-error>
                </td>
              </tr>
            } @else {
              @for (row of preview()?.payload?.rows; track row.row_number) {
                <tr>
                  <td>{{ row.row_number }}</td>
                  <td>{{ row.admission_number || '-' }}</td>
                  <td>{{ Helpers.capitalizeWords(row.name) || '-' }}</td>
                  <td>{{ row.level || '-' }}{{ row.class ? ' / ' + row.class : '' }}</td>
                  <td class="status">
                    <p [ngClass]="row.status">
                      {{ Helpers.toSentenceCase(Helpers.replaceUnderscore(row.status)) }}
                    </p>
                  </td>
                  <td class="reason">{{ row.reason || 'OK' }}</td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>
    </section>

    <div class="import-actions end">
      <button type="button" class="clear-button" (click)="clearUpload()">Start over</button>

      <button
        type="button"
        class="button"
        [disabled]="!preview()?.payload?.summary?.valid || isConfirming()"
        (click)="openConfirm()"
      >
        Import {{ preview()?.payload?.summary?.valid }} student(s)
      </button>
    </div>
  }

  @if (isConfirmed()) {
    <section class="result-panel">
      <div class="result-icon">
        <svg
          width="34"
          height="34"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="9" stroke="#15803d" stroke-width="1.6" />
          <path
            d="M8.5 12.5L11 15L15.5 9.5"
            stroke="#15803d"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <h4>Import completed</h4>
      <small>Batch {{ result()?.payload?.batch_public_id }}</small>

      <div class="summary-cards">
        <div class="summary-card total">
          <h4>{{ result()?.payload?.total ?? 0 }}</h4>
          <p>Total rows</p>
        </div>
        <div class="summary-card valid">
          <h4>{{ result()?.payload?.imported ?? 0 }}</h4>
          <p>Imported</p>
        </div>
        <div class="summary-card duplicate">
          <h4>{{ result()?.payload?.skipped ?? 0 }}</h4>
          <p>Skipped</p>
        </div>
        <div class="summary-card invalid">
          <h4>{{ (result()?.payload?.invalid ?? 0) + (result()?.payload?.failed ?? 0) }}</h4>
          <p>Invalid / failed</p>
        </div>
      </div>

      <div class="import-actions end">
        @if (result()?.payload?.errors_available) {
          <button type="button" class="secondary-button" (click)="downloadErrors()">
            Download error report
          </button>
        }

        <button type="button" class="button" (click)="clearUpload()">Import another file</button>
      </div>
    </section>
  }
</section>

@if (showConfirm()) {
  <section class="pop-up">
    <div class="bg" (click)="showConfirm.set(false)"></div>

    <form (ngSubmit)="confirmImport()">
      <div class="form-heading">
        <div class="image-box">
          <svg
            fill="#fff"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            stroke="#fff"
            stroke-width="0"
          >
            <path
              d="M15.833 3.333H4.167A1.667 1.667 0 0 0 2.5 5v11.667a1.667 1.667 0 0 0 1.667 1.666h11.666a1.667 1.667 0 0 0 1.667-1.666V5a1.667 1.667 0 0 0-1.667-1.667ZM13.333 1.667v3.333M6.667 1.667v3.333M2.5 8.333h15"
              opacity="0"
            />
            <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z" fill="#fff" />
            <path
              d="M12 8v5M12 16h.01"
              stroke="#5C0B13"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <div>
          <h6>Confirm import</h6>
          <small>Review before importing</small>
        </div>
      </div>

      <div class="form-container">
        <p class="confirm-text">
          You are about to import
          <strong>{{ preview()?.payload?.summary?.valid }}</strong>
          student(s) into
          <strong>{{ preview()?.payload?.academic_year?.name }}</strong
          >. Duplicate and invalid rows will be skipped. This action cannot be undone.
        </p>

        <div class="import-actions">
          <button type="button" class="clear-button" (click)="showConfirm.set(false)">
            Cancel
          </button>

          <button type="submit" class="button" [disabled]="isConfirming()">
            @if (isConfirming()) {
              <app-loading-indicator />
            } @else {
              Confirm import
            }
          </button>
        </div>
      </div>
    </form>
  </section>
}
